import { addSentryMetadata } from '../sentrySetup';
import { useStore } from '../store';
import { ConfigurationDto } from '../types/ConfigurationDto';
import { TwitchConfigurationServiceError } from '../types/errors';
import { configurationDtoToConfiguration } from '../types/mappers/configurationMapper';
import { TwitchUserAuth } from '../types/TwitchExtension';

export const useTwitchConfiguration = (): {
	handleAuthorizationReceived: (twitchUserAuth: TwitchUserAuth) => void;
	handleOnChangedConfiguration: () => void;
	handleBroadcastMessage: (
		target: string,
		contentType: string,
		message: string,
	) => void;
} => {
	const store = useStore();

	const handleAuthorizationReceived = (twitchUserAuth: TwitchUserAuth) => {
		store.authorizationReceived(twitchUserAuth);
		store.startLiveStatusCheck();

		try {
			addSentryMetadata({
				channelId: twitchUserAuth.channelId,
				viewerId: twitchUserAuth.userId,
				viewerRole: window.Twitch?.ext.viewer.role,
			});
		} catch {
			console.error('Error adding sentry metadata');
		}
	};

	const handleOnChangedConfiguration = () => {
		if (!window.Twitch?.ext.configuration.broadcaster) {
			store.forceOfflineMode();
			TwitchConfigurationServiceError.report({
				description: 'Configuration broadcaster not found',
			});

			return;
		}

		let configurationDto: ConfigurationDto | undefined = undefined;
		try {
			configurationDto = JSON.parse(
				window.Twitch.ext.configuration.broadcaster.content,
			) as ConfigurationDto;

			const configuration = configurationDtoToConfiguration(configurationDto);
			store.configurationReceived(configuration);
		} catch (error) {
			store.forceOfflineMode();
			TwitchConfigurationServiceError.report({
				description: 'Error parsing configuration',
				cause: error,
				context: {
					configurationDto,
				},
			});

			return;
		}
	};

	const handleBroadcastMessage = (
		target: string,
		contentType: string,
		message: string,
	) => {
		let configurationDto: ConfigurationDto | undefined = undefined;
		try {
			configurationDto = JSON.parse(message) as ConfigurationDto;

			const configuration = configurationDtoToConfiguration(configurationDto);
			store.configurationReceived(configuration);
		} catch (error) {
			store.forceOfflineMode();
			TwitchConfigurationServiceError.report({
				description: 'Error parsing configuration',
				cause: error,
				context: {
					configurationDto,
				},
			});

			return;
		}
	};

	return {
		handleAuthorizationReceived,
		handleOnChangedConfiguration,
		handleBroadcastMessage,
	};
};
